import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import OnboardingProgress from '../components/OnboardingProgress';
import DocumentUpload from '../components/DocumentUpload';
import ApplicationStatus from '../components/ApplicationStatus';
import { DocumentType } from '../utils/documentUpload';

interface FormData {
  // Student Details
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  email: string;
  phone: string;
  studentId: string;
  university: string;

  // Funding Type
  fundingType: 'parent' | 'self' | 'bursary' | 'nsfas';

  // Sponsor Details (not required for NSFAS)
  sponsorFirstName?: string;
  sponsorLastName?: string;
  sponsorEmail?: string;
  sponsorPhone?: string;
  sponsorIdNumber?: string;
  sponsorEmployer?: string;
  sponsorOccupation?: string;
  sponsorMonthlyIncome?: string;
  sponsorWorkAddress?: string;
  sponsorHomeAddress?: string;
  willSignSuretyship?: boolean;

  // Property Selection
  propertyChoice: 'highlands' | 'saratoga';
  leaseDuration: string;
  moveInDate: string;

  // Documents
  documents: {
    [key in DocumentType]?: string;
  };
  termsAccepted: boolean;
}

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studentRecord, setStudentRecord] = useState<{ id: string; status: string; comments?: string } | null>(null);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    phone: '',
    studentId: '',
    university: '',
    fundingType: 'parent',
    propertyChoice: 'saratoga',
    leaseDuration: '1',
    moveInDate: '',
    documents: {},
    termsAccepted: false,
  });

  useEffect(() => {
    const fetchStudentData = async () => {
      if (!user) return;

      try {
        const { data: studentData, error: studentError } = await supabase
          .from('students')
          .select(`
            id,
            application_status,
            comments,
            documents (
              document_type,
              file_url
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (studentError) {
          if (studentError.code === 'PGRST116') {
            // No student record found - this is fine for new users
            return;
          }
          throw studentError;
        }

        if (studentData) {
          setStudentRecord({
            id: studentData.id,
            status: studentData.application_status || 'pending',
            comments: studentData.comments
          });

          // Update form data with existing documents
          if (studentData.documents) {
            const documents = studentData.documents.reduce((acc: Record<string, string>, doc: any) => {
              if (doc.document_type && doc.file_url) {
                acc[doc.document_type] = doc.file_url;
              }
              return acc;
            }, {});
            
            setFormData(prev => ({
              ...prev,
              documents
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
      }
    };

    fetchStudentData();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDocumentUpload = (type: DocumentType, url: string) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [type]: url
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Create or update student record
      const { data: student, error: studentError } = await supabase
        .from('students')
        .upsert({
          user_id: user.id,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          date_of_birth: formData.dateOfBirth,
          phone: formData.phone,
          student_id: formData.studentId,
          university: formData.university,
          funding_type: formData.fundingType,
          application_status: 'pending'
        })
        .select()
        .single();

      if (studentError) throw studentError;

      // Create sponsor record if not NSFAS
      if (formData.fundingType !== 'nsfas' && formData.sponsorFirstName) {
        const { error: sponsorError } = await supabase
          .from('sponsors')
          .upsert({
            student_id: student.id,
            first_name: formData.sponsorFirstName,
            last_name: formData.sponsorLastName,
            email: formData.sponsorEmail,
            phone: formData.sponsorPhone,
            id_number: formData.sponsorIdNumber,
            employer: formData.sponsorEmployer,
            occupation: formData.sponsorOccupation,
            monthly_income: formData.sponsorMonthlyIncome,
            work_address: formData.sponsorWorkAddress,
            home_address: formData.sponsorHomeAddress,
            will_sign_suretyship: formData.willSignSuretyship
          });

        if (sponsorError) throw sponsorError;
      }

      // Create property preference
      const { error: preferenceError } = await supabase
        .from('property_preferences')
        .upsert({
          student_id: student.id,
          property_choice: formData.propertyChoice,
          lease_duration: formData.leaseDuration,
          move_in_date: formData.moveInDate
        });

      if (preferenceError) throw preferenceError;

      // Update user profile to mark onboarding as completed
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Navigate to success page or dashboard
      navigate('/');
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Sponsor Selection</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funding Type
                </label>
                <select
                  name="fundingType"
                  value={formData.fundingType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="parent">Parent/Guardian</option>
                  <option value="self">Self-funded</option>
                  <option value="bursary">Bursary</option>
                  <option value="nsfas">NSFAS</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  University
                </label>
                <input
                  type="text"
                  name="university"
                  value={formData.university}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {formData.fundingType !== 'nsfas' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sponsor First Name
                    </label>
                    <input
                      type="text"
                      name="sponsorFirstName"
                      value={formData.sponsorFirstName || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sponsor Last Name
                    </label>
                    <input
                      type="text"
                      name="sponsorLastName"
                      value={formData.sponsorLastName || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sponsor Email
                    </label>
                    <input
                      type="email"
                      name="sponsorEmail"
                      value={formData.sponsorEmail || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sponsor Phone
                    </label>
                    <input
                      type="tel"
                      name="sponsorPhone"
                      value={formData.sponsorPhone || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Student ID
                </label>
                <input
                  type="text"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Property Selection</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div
                    className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.propertyChoice === 'highlands'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, propertyChoice: 'highlands' }))}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=500"
                      alt="Highlands Lofts"
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                    <h3 className="text-lg font-semibold mb-2">Highlands Lofts Urban Living</h3>
                    <p className="text-gray-600">Modern urban living in the heart of the city</p>
                  </div>

                  <div
                    className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.propertyChoice === 'saratoga'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, propertyChoice: 'saratoga' }))}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=500"
                      alt="21 Saratoga"
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                    <h3 className="text-lg font-semibold mb-2">21 Saratoga</h3>
                    <p className="text-gray-600">Premium student accommodation in a prime location</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lease Duration (months)
                </label>
                <select
                  name="leaseDuration"
                  value={formData.leaseDuration}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={String(i + 1)}>
                      {i + 1} {i === 0 ? 'month' : 'months'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Move-in Date
                </label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Required Documents</h2>

            {studentRecord && (
              <ApplicationStatus
                status={studentRecord.status as 'pending' | 'approved' | 'rejected'}
                comments={studentRecord.comments}
                onDocumentUpload={handleDocumentUpload}
                studentId={studentRecord.id}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DocumentUpload
                documentType="student_id"
                studentId={studentRecord?.id || ''}
                label="Student ID"
                description="Clear photo or scan of your student ID card"
                onUploadComplete={(url) => handleDocumentUpload('student_id', url)}
                onError={(error) => setError(error)}
              />

              {formData.fundingType !== 'nsfas' && (
                <DocumentUpload
                  documentType="sponsor_id"
                  studentId={studentRecord?.id || ''}
                  label="Sponsor ID"
                  description="Clear photo or scan of sponsor's ID document"
                  onUploadComplete={(url) => handleDocumentUpload('sponsor_id', url)}
                  onError={(error) => setError(error)}
                />
              )}

              <DocumentUpload
                documentType="proof_of_residence"
                studentId={studentRecord?.id || ''}
                label="Proof of Residence"
                description="Recent utility bill or bank statement showing residential address"
                onUploadComplete={(url) => handleDocumentUpload('proof_of_residence', url)}
                onError={(error) => setError(error)}
              />

              <DocumentUpload
                documentType="university_registration"
                studentId={studentRecord?.id || ''}
                label="University Registration"
                description="Proof of registration or acceptance letter"
                onUploadComplete={(url) => handleDocumentUpload('university_registration', url)}
                onError={(error) => setError(error)}
              />

              {formData.fundingType !== 'nsfas' && (
                <>
                  <DocumentUpload
                    documentType="sponsor_payslips"
                    studentId={studentRecord?.id || ''}
                    label="Sponsor Payslips"
                    description="Last 3 months' payslips"
                    onUploadComplete={(url) => handleDocumentUpload('sponsor_payslips', url)}
                    onError={(error) => setError(error)}
                  />

                  <DocumentUpload
                    documentType="sponsor_bank_statements"
                    studentId={studentRecord?.id || ''}
                    label="Sponsor Bank Statements"
                    description="Last 3 months' bank statements"
                    onUploadComplete={(url) => handleDocumentUpload('sponsor_bank_statements', url)}
                    onError={(error) => setError(error)}
                  />
                </>
              )}
            </div>

            <div className="border-t pt-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  required
                />
                <span className="text-sm text-gray-700">
                  I agree to the terms and conditions of the student housing agreement. I confirm that all information provided is accurate and all documents uploaded are authentic.
                </span>
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                {error}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-12">
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 px-6 rounded-t-2xl">
        <h1 className="text-3xl font-bold mb-2">Student Onboarding</h1>
        <p className="text-blue-100">Complete the form below to begin your student housing journey</p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white shadow-xl rounded-b-2xl p-8">
        <OnboardingProgress
          currentStep={currentStep}
          totalSteps={4}
          steps={[
            { title: 'Sponsor', completed: currentStep > 1 },
            { title: 'Personal', completed: currentStep > 2 },
            { title: 'Property', completed: currentStep > 3 },
            { title: 'Documents', completed: currentStep > 4 }
          ]}
        />
        
        <div className="mb-8">
          {renderStepContent()}
        </div>

        <div className="flex justify-between items-center">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="ml-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              disabled={!formData.termsAccepted || loading}
              className={`ml-auto px-6 py-2 rounded-lg transition-colors ${
                formData.termsAccepted && !loading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default Onboarding;